import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

const TIER_LABEL = { blocked: "blocked", daily: "due now", weekly: "due this week", upcoming: "coming up", initial: "to complete" };

export default function MyTrainingsCard() {
  const navigate = useNavigate();
  const [radar, setRadar] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/training/my-radar").then((r) => setRadar(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed) return null;
  const summary = radar?.summary || {};
  const dueNow = (summary.blocked || 0) + (summary.daily || 0);
  const dueWeek = summary.weekly || 0;
  const upcoming = summary.upcoming || 0;
  const total = dueNow + dueWeek + upcoming;
  const topItems = (radar?.groups || []).flatMap((g) => g.items).slice(0, 3);
  const initialCount = (radar?.summary?.total_items || 0) - total;

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="my-trainings-card">
      <img src="/training-art/learning_band.jpeg" alt="" aria-hidden="true" loading="lazy" className="h-28 w-full object-cover sm:h-36" />
      <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-2.5 font-heading text-sm font-semibold text-stone-900">
              <GraduationCap className="h-5 w-5 text-primary" /> My trainings
            </p>
            {!radar ? (
              <p className="mt-1 text-xs text-stone-500">Checking your training status…</p>
            ) : total === 0 && topItems.length === 0 ? (
              <p className="mt-1 text-xs font-medium text-[#2F6B4F]" data-testid="my-trainings-clear">
                You're all caught up — new modules land in your library over time.
              </p>
            ) : (
              <p className="mt-1 text-xs text-stone-500" data-testid="my-trainings-status">
                {dueNow > 0 && <span className="font-semibold text-[#C0392B]">{dueNow} due now</span>}
                {dueNow > 0 && (dueWeek > 0 || upcoming > 0) && " · "}
                {dueWeek > 0 && <span className="font-semibold text-[#B7791F]">{dueWeek} due this week</span>}
                {dueWeek > 0 && upcoming > 0 && " · "}
                {upcoming > 0 && <span>{upcoming} coming up</span>}
                {total === 0 && initialCount > 0 && <span className="font-medium text-stone-600">{initialCount} to complete in your library</span>}
              </p>
            )}
          </div>
          <Button onClick={() => navigate("/training")} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="my-trainings-open">
            Open my trainings <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
        {topItems.length > 0 && (
          <div className="mt-3 divide-y divide-stone-50 border-t border-stone-100" data-testid="my-trainings-due-list">
            {topItems.map((t, i) => (
              <button key={i} onClick={() => navigate("/training")}
                className="flex w-full items-center justify-between gap-3 px-1 py-2.5 text-left transition-colors hover:bg-primary/5"
                data-testid={`my-trainings-item-${i}`}>
                <span className="text-sm font-medium text-stone-800">{t.title}</span>
                <span className={`shrink-0 text-xs font-semibold ${t.tier === "blocked" || t.tier === "daily" ? "text-[#C0392B]" : t.tier === "weekly" ? "text-[#B7791F]" : "text-stone-400"}`}>
                  {TIER_LABEL[t.tier] || t.tier}{t.days_left != null && t.days_left >= 0 ? ` · ${t.days_left}d left` : ""}
                </span>
              </button>
            ))}
            {(radar?.summary?.total_items || 0) > 3 && (
              <p className="px-1 pt-2 text-xs text-stone-400">+ {radar.summary.total_items - 3} more in your training center</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
